import { ReinforcementTopicState } from '../types';

// How the overall evaluation score blends the three signals. Only the
// components that actually have data are used — the weights are renormalized
// over whatever is present (e.g. no quiz yet → understanding + curiosity only).
// Tweak these three numbers to reweight the score everywhere it's shown.
export const SCORE_WEIGHTS = { understanding: 0.45, quiz: 0.4, curiosity: 0.15 };

// Understanding contribution of a single topic's mastery state (0–1).
export const STATE_SCORE: Record<ReinforcementTopicState, number> = {
  solid: 1,
  partly_solid: 0.5,
  forgotten: 0,
  never_learned: 0,
};

export interface ScorePart {
  key: string;
  label: string;
  value: number;
  weight: number;
}

export interface StudentScore {
  /** The blended 0–100 score, or null when no component has data. */
  score: number | null;
  understanding: number | null;
  quiz: number | null;
  curiosity: number | null;
  /** The components that actually contributed — drives the breakdown UI. */
  parts: ScorePart[];
}

/** Understanding % = mastery mix across the topics the student has chatted about. */
export const understandingPctFromTopics = (
  topics: { state: ReinforcementTopicState }[]
): number | null => {
  if (topics.length === 0) return null;
  const sum = topics.reduce((a, t) => a + (STATE_SCORE[t.state] ?? 0), 0);
  return Math.round((sum / topics.length) * 100);
};

/**
 * The combined evaluation score, shared by the class dashboard leaderboard and
 * the per-student report so a student's number reads the same in both places.
 */
export const computeStudentScore = (opts: {
  topics: { state: ReinforcementTopicState }[];
  quizAvg: number | null;
  curiosityAvg: number | null;
}): StudentScore => {
  const understanding = understandingPctFromTopics(opts.topics);
  const quiz = opts.quizAvg;
  const curiosity = opts.curiosityAvg;

  const raw = [
    { key: 'understanding', label: 'Understanding', value: understanding, weight: SCORE_WEIGHTS.understanding },
    { key: 'quiz', label: 'Quiz scores', value: quiz, weight: SCORE_WEIGHTS.quiz },
    { key: 'curiosity', label: 'Curiosity', value: curiosity, weight: SCORE_WEIGHTS.curiosity },
  ];
  const present = raw.filter((p): p is ScorePart => p.value != null);
  if (present.length === 0) {
    return { score: null, understanding, quiz, curiosity, parts: [] };
  }
  const totalWeight = present.reduce((a, p) => a + p.weight, 0);
  const score = Math.round(
    present.reduce((a, p) => a + p.value * (p.weight / totalWeight), 0)
  );
  return { score, understanding, quiz, curiosity, parts: present };
};
